import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AmountInput from '../shared/AmountInput';
import LoadingSpinner from '../Loading/LoadingSpinner';
import { winamaxAPI } from '../../services/api';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, BarElement, BarController, Filler, Tooltip, Legend);

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const MONTHS_SHORT = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];

function mk(y, m) { return `${y}-${String(m).padStart(2, '0')}`; }

function entriesToMonths(entries) {
  const months = {};
  for (const e of entries) {
    const d = new Date(e.date);
    const key = mk(d.getFullYear(), d.getMonth() + 1);
    if (!months[key]) months[key] = { depots: [], recettes: [] };
    months[key][e.type === 'depot' ? 'depots' : 'recettes'].push({ ...e, date: new Date(e.date).toISOString().slice(0, 10) });
  }
  return months;
}
// ── Modal ──────────────────────────────────────────────────────────────────────
function WmModal({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── MonthYearSelector ──────────────────────────────────────────────────────────
function MonthYearSelector({ year, month, years, onYearChange, onMonthChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <select value={year} onChange={e => onYearChange(parseInt(e.target.value))} className="filter-select">
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select value={month} onChange={e => onMonthChange(parseInt(e.target.value))} className="filter-select">
        {MONTHS_FR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
      </select>
    </div>
  );
}

// ── AmountBadge ────────────────────────────────────────────────────────────────
function AmountBadge({ value, size = 'sm' }) {
  const pos = value >= 0;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: size === 'lg' ? '6px 16px' : '3px 10px',
      borderRadius: 999,
      background: pos ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
      color: pos ? '#22c55e' : '#ef4444',
      border: `1px solid ${pos ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
      fontSize: size === 'lg' ? 20 : 12,
      fontWeight: 500,
      fontFamily: 'monospace',
    }}>
      {pos ? '+' : '-'}{Math.abs(value).toFixed(2)} €
    </span>
  );
}

// ── SummaryCard ────────────────────────────────────────────────────────────────
function SummaryCard({ label, value, colorType }) {
  const isAuto = colorType === 'auto';
  const isRed = colorType === 'red' || (isAuto && value < 0);
  const isGreen = colorType === 'green' || (isAuto && value >= 0);
  const color = isRed ? '#ef4444' : isGreen ? '#22c55e' : 'var(--text)';
  const prefix = colorType === 'red' ? '-' : colorType === 'green' ? '+' : (value >= 0 ? '+' : '-');
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color, fontFamily: 'monospace', fontSize: 18 }}>
        {prefix}{Math.abs(value).toFixed(2)} €
      </div>
    </div>
  );
}

// ── EntryRow ──────────────────────────────────────────────────────────────────
function EntryRow({ entry, type, onDelete, onEdit }) {
  const isDepot = type === 'depot';
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onEdit && onEdit(entry)}
      style={{
        display: 'grid', gridTemplateColumns: '110px 1fr auto auto', gap: 10, alignItems: 'center',
        padding: '9px 12px', borderRadius: 8, marginBottom: 4,
        background: hover ? 'var(--bg3)' : 'transparent',
        transition: 'background 0.1s',
        cursor: 'pointer',
      }}
    >
      <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{entry.date}</span>
      <span style={{ fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.description}</span>
      <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500, color: isDepot ? '#ef4444' : '#22c55e', whiteSpace: 'nowrap' }}>
        {isDepot ? '-' : '+'}{entry.montant.toFixed(2)} €
      </span>
      <button
        onClick={e => { e.stopPropagation(); onDelete(entry._id); }}
        tabIndex={hover ? 0 : -1}
        style={{
          background: 'none', border: 'none', color: 'var(--text3)',
          cursor: 'pointer', fontSize: 14, opacity: hover ? 1 : 0, transition: 'opacity 0.15s',
          padding: '2px 4px',
        }}
      >✕</button>
    </div>
  );
}

// ── AddEntryModal ──────────────────────────────────────────────────────────────
function AddEntryModal({ onClose, onSave, type, initialEntry }) {
  const [currentType, setCurrentType] = useState(type);
  const isDepot = currentType === 'depot';
  const isEdit = initialEntry?._id != null;
  const [date, setDate] = useState(initialEntry?.date ?? new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState(initialEntry?.description ?? '');
  const [montant, setMontant] = useState(initialEntry?.montant != null ? String(initialEntry.montant) : '');

  function handleSubmit(e) {
    e.preventDefault();
    if (!montant) return;
    const payload = { date, description: description || (isDepot ? 'ApplePay' : 'Virement'), montant: parseFloat(montant) };
    if (isEdit) payload._id = initialEntry._id;
    onSave(payload, currentType);
    onClose();
  }

  const title = isEdit ? `Modifier ${isDepot ? 'le dépôt' : 'la recette'}` : (isDepot ? '+ Nouveau dépôt' : '+ Nouvelle recette');

  return (
    <WmModal open={true} onClose={onClose} title={title}>
      <div style={{ display: 'flex', marginBottom: 20, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border2)', cursor: 'pointer' }}>
        <div onClick={() => setCurrentType('depot')} style={{ flex: 1, textAlign: 'center', padding: '10px', background: isDepot ? 'rgba(239,68,68,0.12)' : 'transparent', color: isDepot ? '#ef4444' : 'var(--text2)', fontSize: 13, fontWeight: 600 }}>
          ↓ Dépôt
        </div>
        <div onClick={() => setCurrentType('recette')} style={{ flex: 1, textAlign: 'center', padding: '10px', background: !isDepot ? 'rgba(34,197,94,0.12)' : 'transparent', color: !isDepot ? '#22c55e' : 'var(--text2)', fontSize: 13, fontWeight: 600 }}>
          ↑ Recette
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="modal-fields">
          <div className="field-group">
            <label className="field-label">Montant (€)</label>
            <AmountInput value={montant} onChange={setMontant} />
          </div>
          <div className="field-group">
            <label className="field-label">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="field-input" style={{ colorScheme: 'dark' }} required />
          </div>
          <div className="field-group">
            <label className="field-label">Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder={isDepot ? 'ApplePay (par défaut)' : 'Virement (par défaut)'}
              className="field-input" />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose} className="btn-ghost">Annuler</button>
          <button type="submit" className={`wm-submit-btn${isDepot ? ' depot' : ' recette'}`} style={{ transition: 'background 0.2s' }}>
            {isEdit ? 'Mettre à jour' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </WmModal>
  );
}

// ── ChartCanvas ────────────────────────────────────────────────────────────────
function ChartCanvas({ setup, deps }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    const t = setTimeout(() => {
      if (canvasRef.current) chartRef.current = setup(canvasRef.current);
    }, 50);
    return () => {
      clearTimeout(t);
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, setup]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />;
}

// ── RECAP TAB ─────────────────────────────────────────────────────────────────
function RecapTab({ yearlyStats, totalWinamax, totalDepots, totalRecettes }) {
  const maxAbs = Math.max(...yearlyStats.map(y => Math.abs(y.total)), 1);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Récapitulatif</h1>
          <p className="page-sub">Bilan global de vos paris sportifs</p>
        </div>
      </div>

      {/* Balance hero */}
      <div className="balance-hero" style={{
        background: 'linear-gradient(135deg, rgba(249,115,22,0.12) 0%, rgba(249,115,22,0.04) 100%)',
        border: '1px solid rgba(249,115,22,0.25)',
        marginBottom: 28,
      }}>
        <div className="balance-label" style={{ color: 'rgba(249,115,22,0.80)' }}>Total Winamax · Cumul</div>
        <div className="balance-amount" style={{ color: totalWinamax >= 0 ? '#4ade80' : '#f87171' }}>
          {totalWinamax >= 0 ? '+' : ''}{Math.abs(totalWinamax).toFixed(2)} €
        </div>
        <div className="balance-sub" style={{ color: 'rgba(249,115,22,0.55)' }}>
          {yearlyStats.length} année{yearlyStats.length !== 1 ? 's' : ''} suivie{yearlyStats.length !== 1 ? 's' : ''}
          {(totalDepots > 0 || totalRecettes > 0) && (
            <>&nbsp;·&nbsp;
              <span style={{ color: '#4ade80' }}>↑ {totalRecettes.toFixed(2)} €</span>
              &nbsp;
              <span style={{ color: '#f87171' }}>↓ {totalDepots.toFixed(2)} €</span>
            </>
          )}
        </div>
      </div>

      {/* Yearly table */}
      {yearlyStats.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '48px 24px',
          textAlign: 'center', color: 'var(--text3)',
        }}>
          <p style={{ fontSize: 14 }}>Aucune transaction enregistrée.</p>
          <p style={{ fontSize: 12, marginTop: 8 }}>
            Ajoutez des dépôts et recettes dans l'onglet Transactions pour voir vos statistiques ici.
          </p>
        </div>
      ) : (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div className="wm-recap-head">
            {['Année', 'Performance', 'Résultat'].map((h, i) => (
              <span key={i} className="stat-label" style={{ margin: 0 }}>{h}</span>
            ))}
          </div>
          {yearlyStats.map((yr, i) => (
            <div
              key={yr.year}
              className="wm-recap-row"
              style={{ borderBottom: i < yearlyStats.length - 1 ? '1px solid var(--border)' : 'none' }}
            >
              <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 14 }}>{yr.year}</span>
              <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, (Math.abs(yr.total) / maxAbs) * 100)}%`,
                  background: yr.total >= 0
                    ? 'linear-gradient(90deg, #22c55e, rgba(34,197,94,0.5))'
                    : 'linear-gradient(90deg, #ef4444, rgba(239,68,68,0.5))',
                  borderRadius: 3,
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <AmountBadge value={yr.total} />
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

// ── TRANSACTIONS TAB ──────────────────────────────────────────────────────────
function TransactionsTab({ data, onSaveEntry, onDeleteEntry }) {
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  // activeEntry: null = closed, { type, entry: null } = create, { type, entry: {...} } = edit
  const [activeEntry, setActiveEntry] = useState(null);

  const years = useMemo(() => {
    const currentYear = now.getFullYear();
    const ys = new Set(Object.keys(data.months).map(k => parseInt(k.split('-')[0])));
    for (let y = 2019; y <= currentYear + 1; y++) ys.add(y);
    return [...ys].sort((a, b) => b - a);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.months]);

  const monthKey = mk(selYear, selMonth);
  const monthData = data.months[monthKey] || { depots: [], recettes: [] };
  const totalDepots = monthData.depots.reduce((s, d) => s + d.montant, 0);
  const totalRecettes = monthData.recettes.reduce((s, r) => s + r.montant, 0);
  const net = totalRecettes - totalDepots;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="page-sub">Dépôts et recettes par mois</p>
        </div>
        <MonthYearSelector year={selYear} month={selMonth} years={years} onYearChange={setSelYear} onMonthChange={setSelMonth} />
      </div>

      <div className="stats-row" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        <SummaryCard label="Dépôt Total" value={totalDepots} colorType="red" />
        <SummaryCard label="Recette Total" value={totalRecettes} colorType="green" />
        <SummaryCard label="Total du mois" value={net} colorType="auto" />
      </div>

      <div className="wm-tx-cols">
        {/* Dépôts */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div className="cat-col-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Dépôts</span>
              <span className="section-count">{monthData.depots.length}</span>
            </div>
            <button className="wm-col-add-btn depot" onClick={() => setActiveEntry({ type: 'depot', entry: null })}>+ Ajouter</button>
          </div>
          <div style={{ padding: '8px 4px', minHeight: 80 }}>
            {monthData.depots.length === 0
              ? <p style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '24px 0' }}>Aucun dépôt ce mois</p>
              : monthData.depots.map(d => (
                  <EntryRow key={d._id} entry={d} type="depot"
                    onDelete={id => onDeleteEntry('depot', id)}
                    onEdit={entry => setActiveEntry({ type: 'depot', entry })}
                  />
                ))
            }
          </div>
        </div>

        {/* Recettes */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div className="cat-col-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Recettes</span>
              <span className="section-count">{monthData.recettes.length}</span>
            </div>
            <button className="wm-col-add-btn recette" onClick={() => setActiveEntry({ type: 'recette', entry: null })}>+ Ajouter</button>
          </div>
          <div style={{ padding: '8px 4px', minHeight: 80 }}>
            {monthData.recettes.length === 0
              ? <p style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '24px 0' }}>Aucune recette ce mois</p>
              : monthData.recettes.map(r => (
                  <EntryRow key={r._id} entry={r} type="recette"
                    onDelete={id => onDeleteEntry('recette', id)}
                    onEdit={entry => setActiveEntry({ type: 'recette', entry })}
                  />
                ))
            }
          </div>
        </div>
      </div>

      {activeEntry !== null && (
        <AddEntryModal
          type={activeEntry.type}
          initialEntry={activeEntry.entry}
          onClose={() => setActiveEntry(null)}
          onSave={(entry, newType) => {
            onSaveEntry(entry, newType);
            setActiveEntry(null);
          }}
        />
      )}
    </div>
  );
}

// ── GRAPHIQUES TAB ────────────────────────────────────────────────────────────
function GraphiquesTab({ data, yearlyStats }) {
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());

  const years = useMemo(() => {
    const ys = new Set(yearlyStats.map(y => y.year));
    ys.add(now.getFullYear());
    return [...ys].sort((a, b) => b - a);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearlyStats]);

  const totalWinamax = useMemo(() =>
    yearlyStats.reduce((s, y) => s + y.total, 0),
    [yearlyStats]
  );

  const cumulativeData = useMemo(() => {
    let cumul = 0;
    return yearlyStats.map(yr => {
      cumul += yr.total;
      return { label: String(yr.year), cumul: parseFloat(cumul.toFixed(2)) };
    });
  }, [yearlyStats]);

  const monthlyData = useMemo(() => {
    return MONTHS_SHORT.map((label, i) => {
      const key = mk(selYear, i + 1);
      const md = data.months[key];
      const depots = md ? md.depots.reduce((s, d) => s + d.montant, 0) : 0;
      const recettes = md ? md.recettes.reduce((s, r) => s + r.montant, 0) : 0;
      return {
        label,
        depots: parseFloat(depots.toFixed(2)),
        recettes: parseFloat(recettes.toFixed(2)),
        net: parseFloat((recettes - depots).toFixed(2)),
      };
    });
  }, [data, selYear]);

  const gridColor = 'rgba(255,255,255,0.05)';
  const tickColor = 'rgba(255,255,255,0.35)';
  const baseOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#181b2e', borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1,
        titleColor: '#f0f0f8', bodyColor: '#f0f0f8',
        callbacks: { label: ctx => `${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)} €` }
      }
    },
    scales: {
      x: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 } } },
      y: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 }, callback: v => `${v >= 0 ? '+' : ''}${v}€` } }
    }
  };

  const setupCumul = useCallback(canvas => new Chart(canvas, {
    type: 'line',
    data: {
      labels: cumulativeData.map(d => d.label),
      datasets: [{
        data: cumulativeData.map(d => d.cumul),
        borderColor: '#f97316',
        backgroundColor: ctx => {
          const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 220);
          g.addColorStop(0, 'rgba(249,115,22,0.22)');
          g.addColorStop(1, 'rgba(249,115,22,0)');
          return g;
        },
        borderWidth: 2.5, fill: true, tension: 0.35,
        pointBackgroundColor: '#f97316', pointRadius: 5, pointHoverRadius: 7,
      }]
    },
    options: {
      ...baseOpts,
      plugins: {
        ...baseOpts.plugins,
        tooltip: {
          ...baseOpts.plugins.tooltip,
          callbacks: { label: ctx => `Cumulé : ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)} €` }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [cumulativeData]);

  const setupMonthly = useCallback(canvas => {
    return new Chart(canvas, {
      type: 'bar',
      data: {
        labels: monthlyData.map(d => d.label),
        datasets: [
          { label: 'Dépôts',   data: monthlyData.map(d => d.depots),   backgroundColor: 'rgba(239,68,68,0.60)',  borderRadius: 4, borderSkipped: false },
          { label: 'Recettes', data: monthlyData.map(d => d.recettes), backgroundColor: 'rgba(34,197,94,0.60)',  borderRadius: 4, borderSkipped: false },
          { label: 'Net',      data: monthlyData.map(d => d.net),      backgroundColor: 'rgba(249,115,22,0.65)', borderRadius: 4, borderSkipped: false },
        ]
      },
      options: {
        ...baseOpts,
        plugins: {
          ...baseOpts.plugins,
          legend: { display: true, labels: { color: tickColor, font: { size: 11 }, boxWidth: 12, boxHeight: 12 } },
          tooltip: { ...baseOpts.plugins.tooltip, callbacks: { label: ctx => `${ctx.dataset.label} : ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)} €` } }
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyData]);

  const setupTotalWm = useCallback(canvas => {
    const totalDepots   = parseFloat(monthlyData.reduce((s, m) => s + m.depots,   0).toFixed(2));
    const totalRecettes = parseFloat(monthlyData.reduce((s, m) => s + m.recettes, 0).toFixed(2));
    const totalNet      = parseFloat((totalRecettes - totalDepots).toFixed(2));
    const TC = 'rgba(251,191,36,';
    return new Chart(canvas, {
      type: 'bar',
      data: {
        labels: ['Total'],
        datasets: [
          { label: 'Dépôts',   data: [totalDepots],   backgroundColor: `${TC}0.70)`, borderRadius: 4, borderSkipped: false },
          { label: 'Recettes', data: [totalRecettes], backgroundColor: `${TC}0.85)`, borderRadius: 4, borderSkipped: false },
          { label: 'Net',      data: [totalNet],      backgroundColor: `${TC}1.0)`,  borderRadius: 4, borderSkipped: false },
        ]
      },
      options: {
        ...baseOpts,
        plugins: {
          ...baseOpts.plugins,
          legend: { display: false },
          tooltip: { ...baseOpts.plugins.tooltip, callbacks: { label: ctx => `${ctx.dataset.label} : ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)} €` } }
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyData]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Graphiques</h1>
          <p className="page-sub">Analysez vos paris en un coup d'œil</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Chart 1 — Gains cumulés */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
          <div style={{ marginBottom: 16 }}>
            <div className="chart-title">Gains cumulés</div>
            <div className="chart-sub">Évolution depuis le début</div>
          </div>
          <div style={{ height: 240 }}>
            {cumulativeData.length > 0
              ? <ChartCanvas setup={setupCumul} deps={[cumulativeData]} />
              : <p style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', paddingTop: 80 }}>Aucune donnée à afficher</p>
            }
          </div>
        </div>

        {/* Chart 2 — Mensuel */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div className="chart-title">Dépôts & Recettes mensuels</div>
              <div className="chart-sub">Mois par mois — {selYear}</div>
            </div>
            <select value={selYear} onChange={e => setSelYear(parseInt(e.target.value))} className="filter-select">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', height: 240 }}>
            <div style={{ flex: 3, minWidth: 0 }}>
              <ChartCanvas setup={setupMonthly} deps={[monthlyData]} />
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 6px' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <ChartCanvas setup={setupTotalWm} deps={[monthlyData]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── WinamaxPage ───────────────────────────────────────────────────────────────
const WinamaxPage = () => {
  const location = useLocation();
  const activeTab = location.pathname.endsWith('transactions') ? 'transactions'
    : location.pathname.endsWith('graphiques') ? 'graphiques'
    : 'recap';

  const [data, setData] = useState({ months: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    winamaxAPI.list()
      .then(res => setData({ months: entriesToMonths(res.data.data) }))
      .catch((e) => { console.error('Erreur chargement Winamax', e); })
      .finally(() => setLoading(false));
  }, []);

  const handleSaveEntry = useCallback(async (entry, type) => {
    try {
      if (entry._id) {
        const res = await winamaxAPI.update(entry._id, {
          type,
          date: entry.date,
          description: entry.description,
          montant: entry.montant,
        });
        const updated = { ...res.data.data, date: new Date(res.data.data.date).toISOString().slice(0, 10) };
        setData(d => {
          const months = {};
          for (const [k, v] of Object.entries(d.months)) {
            months[k] = {
              depots:   v.depots.filter(e => e._id !== entry._id),
              recettes: v.recettes.filter(e => e._id !== entry._id),
            };
          }
          const d2 = new Date(updated.date);
          const key = mk(d2.getFullYear(), d2.getMonth() + 1);
          if (!months[key]) months[key] = { depots: [], recettes: [] };
          months[key][type === 'depot' ? 'depots' : 'recettes'].push(updated);
          return { months };
        });
      } else {
        const res = await winamaxAPI.create({
          type,
          date: entry.date,
          description: entry.description,
          montant: entry.montant,
        });
        const created = { ...res.data.data, date: new Date(res.data.data.date).toISOString().slice(0, 10) };
        setData(d => {
          const d2 = new Date(created.date);
          const key = mk(d2.getFullYear(), d2.getMonth() + 1);
          const months = { ...d.months };
          if (!months[key]) months[key] = { depots: [], recettes: [] };
          const bucket = type === 'depot' ? 'depots' : 'recettes';
          months[key] = { ...months[key], [bucket]: [...months[key][bucket], created] };
          return { months };
        });
      }
    } catch (e) {
      console.error('Erreur lors de la sauvegarde Winamax', e);
    }
  }, []);

  const handleDeleteEntry = useCallback(async (type, id) => {
    try {
      await winamaxAPI.remove(id);
      const bucket = type === 'depot' ? 'depots' : 'recettes';
      setData(d => {
        const months = {};
        for (const [k, v] of Object.entries(d.months)) {
          months[k] = { ...v, [bucket]: v[bucket].filter(e => e._id !== id) };
        }
        return { months };
      });
    } catch (e) {
      console.error('Erreur lors de la suppression Winamax', e);
    }
  }, []);

  const yearlyStats = useMemo(() => {
    const byYear = {};
    Object.entries(data.months).forEach(([key, md]) => {
      const year = parseInt(key.split('-')[0]);
      if (!byYear[year]) byYear[year] = { depots: 0, recettes: 0 };
      byYear[year].depots   += (md.depots || []).reduce((s, d) => s + d.montant, 0);
      byYear[year].recettes += (md.recettes || []).reduce((s, r) => s + r.montant, 0);
    });
    return Object.entries(byYear)
      .map(([year, { depots, recettes }]) => ({
        year: parseInt(year),
        depots,
        recettes,
        total: parseFloat((recettes - depots).toFixed(2)),
      }))
      .sort((a, b) => a.year - b.year);
  }, [data.months]);

  const totalWinamax  = useMemo(() => yearlyStats.reduce((s, y) => s + y.total,    0), [yearlyStats]);
  const totalDepots   = useMemo(() => yearlyStats.reduce((s, y) => s + y.depots,   0), [yearlyStats]);
  const totalRecettes = useMemo(() => yearlyStats.reduce((s, y) => s + y.recettes, 0), [yearlyStats]);

  if (loading) return <LoadingSpinner message="Chargement Winamax..." />;

  return (
    <div className="page-content">
      {activeTab === 'recap'        && <RecapTab yearlyStats={yearlyStats} totalWinamax={totalWinamax} totalDepots={totalDepots} totalRecettes={totalRecettes} />}
      {activeTab === 'transactions' && <TransactionsTab data={data} onSaveEntry={handleSaveEntry} onDeleteEntry={handleDeleteEntry} />}
      {activeTab === 'graphiques'   && <GraphiquesTab data={data} yearlyStats={yearlyStats} />}
    </div>
  );
};

export default WinamaxPage;
